import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, TextField, Checkbox,
  InputAdornment, Stack, IconButton, Button, Box, Typography
} from '@mui/material';
import { Search, AddShoppingCart, CheckCircle } from '@mui/icons-material';
import API from '../../services/api';

const normalizeText = (value) => {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

const buildSearchText = (code, desc) => normalizeText(`${code || ''} ${desc || ''}`);

export default function PickPromotion({ 
  selectedPromotions, 
  setSelectedPromotions,
  handleAddPromotions,
  excludedProductIds
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [products, setProducts] = useState([])

  const getProductData = async () => {
    try {
      const response = await API.get('product/');
      setProducts(response.data);
    } catch (error) {
      console.error(error);
    }
    
  };
  
    React.useEffect(() => {
        getProductData();
    }, []);

  React.useEffect(() => {
    if (!search) {
      setDebouncedSearch('');
      return;
    }
    const handle = setTimeout(() => setDebouncedSearch(search), 200);
    return () => clearTimeout(handle);
  }, [search]);

  const searchTokens = React.useMemo(() => {
    const text = normalizeText(debouncedSearch);
    if (!text) return [];
    return text.split(/\s+/).filter(Boolean);
  }, [debouncedSearch]);

  const excludedSet = React.useMemo(() => {
    if (!excludedProductIds) return new Set();
    if (excludedProductIds instanceof Set) return excludedProductIds;
    return new Set(excludedProductIds);
  }, [excludedProductIds]);

  const indexedProducts = React.useMemo(() => (
    (products || []).map((product) => ({
      product,
      search: buildSearchText(product?.code, product?.description),
    }))
  ), [products]);

  const filteredProducts = React.useMemo(() => {
    const tokens = searchTokens;
    const matches = (text) => (tokens.length === 0) || tokens.every((tok) => text.includes(tok));
    return indexedProducts
      .filter(({ product, search }) => !excludedSet.has(product?.id) && matches(search))
      .map(({ product }) => product);
  }, [indexedProducts, searchTokens, excludedSet]);

  React.useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const filteredIdSet = React.useMemo(() => {
    return new Set(filteredProducts.map((p) => p.id));
  }, [filteredProducts]);

  const isAllFilteredSelected = React.useMemo(() => {
    if (filteredProducts.length === 0) return false;
    return filteredProducts.every((p) => selectedPromotions.some((s) => s.id === p.id));
  }, [filteredProducts, selectedPromotions]);

  const handleToggleAll = React.useCallback(() => {
    if (filteredProducts.length === 0) return;
    if (isAllFilteredSelected) {
      setSelectedPromotions((prev) => prev.filter((p) => !filteredIdSet.has(p.id)));
      return;
    }
    setSelectedPromotions((prev) => {
      const existing = new Set(prev.map((p) => p.id));
      const next = [...prev];
      filteredProducts.forEach((p) => {
        if (!existing.has(p.id)) next.push(p);
      });
      return next;
    });
  }, [filteredProducts, filteredIdSet, isAllFilteredSelected, setSelectedPromotions]);

  const handleSelectProduct = (product) => {
    setSelectedPromotions(prev => 
      prev.some(p => p.id === product.id) 
        ? prev.filter(p => p.id !== product.id) 
        : [...prev, product]
    );
  };

  return (
    <Paper sx={{  width: '100%' }}>
      {/* PARTE SUPERIOR CON EL MISMO DISEÑO QUE PICKPRODUCT */}
      <Box sx={{
        p: 2,
        backgroundColor: 'white',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography 
          variant="h6" 
          sx={{ 
            display: { xs: 'block', md: 'none' },
            mb: 2,
            fontWeight: 600
          }}
        >
          Seleccionar promociones
        </Typography>

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' }
        }}>
          
          {/* Campo de búsqueda - Igual que PickProduct */}
          <TextField
            placeholder="Buscar productos promocionales..."
            variant="outlined"
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white'
              }
            }}
          />

          {/* Botones - Mismo diseño que PickProduct */}
          <Box sx={{
            display: 'flex',
            gap: 1,
            width: { xs: '100%', md: 'auto' }
          }}>
            <Box sx={{ position: 'relative' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleToggleAll}
                disabled={filteredProducts.length === 0}
                sx={{
                  height: 36,
                  px: 2,
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: isAllFilteredSelected ? '#4CAF50' : 'grey.700',
                  textTransform: 'none',
                  borderRadius: 0.75,
                  borderColor: isAllFilteredSelected ? '#4CAF50' : 'grey.300',
                  backgroundColor: 'white',
                  minWidth: 140,
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  zIndex: 1,
                  '&:hover': {
                    borderColor: isAllFilteredSelected ? '#388E3C' : 'grey.400',
                    backgroundColor: isAllFilteredSelected ? 'rgba(76, 175, 80, 0.04)' : 'rgba(0, 0, 0, 0.02)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  },
                  '&:active': {
                    transform: 'translateY(0)'
                  },
                  '&:disabled': {
                    color: 'grey.400',
                    borderColor: 'grey.200',
                    backgroundColor: 'grey.50',
                    transform: 'none',
                    boxShadow: 'none'
                  }
                }}
              >
                {isAllFilteredSelected ? 'Deseleccionar todo' : 'Seleccionar todo'}
              </Button>
              
              {isAllFilteredSelected && (
                <CheckCircle
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    fontSize: 16,
                    color: '#4CAF50',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    padding: 0.5,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    zIndex: 2
                  }}
                />
              )}
            </Box>

            <Box sx={{ position: 'relative' }}>
              <IconButton
                size="small"
                onClick={handleAddPromotions}
                disabled={selectedPromotions.length === 0}
                sx={{
                  height: 34,
                  width: 34,
                  backgroundColor: selectedPromotions.length > 0 ? 'success.main' : 'transparent',
                  color: selectedPromotions.length > 0 ? 'white' : 'grey.600',
                  border: '1px solid',
                  borderColor: selectedPromotions.length > 0 ? 'success.main' : 'grey.300',
                  '&:hover': selectedPromotions.length > 0 ? {
                    backgroundColor: 'success.dark',
                    borderColor: 'success.dark'
                  } : {
                    backgroundColor: 'grey.100'
                  },
                  '&:disabled': {
                    borderColor: 'grey.200',
                    color: 'grey.400'
                  }
                }}
              >
                <AddShoppingCart fontSize="small" />
              </IconButton>

              {/* Badge con contador */}
              {selectedPromotions.length > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    minWidth: 18,
                    height: 18,
                    backgroundColor: 'white',
                    color: 'success.dark',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    border: '1.5px solid',
                    borderColor: 'success.main',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  {selectedPromotions.length}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {/* FIN DE LA PARTE SUPERIOR - TODO LO DEMÁS IGUAL */}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width="60px"></TableCell>
              <TableCell>Código</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="right">Precio</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((product) => (
                <TableRow 
                  key={product.id}
                  hover
                  onClick={() => handleSelectProduct(product)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedPromotions.some(p => p.id === product.id)}
                      onChange={() => handleSelectProduct(product)}
                    />
                  </TableCell>
                  <TableCell>{product.code}</TableCell>
                  <TableCell>{product.description}</TableCell>
                  <TableCell align="right">
                    {product.prices[0]?.price || 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredProducts.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Paper>
  );
}
